import crypto from 'crypto'

import OrdersStatusApi from '@/api/order/status'
import MerchantOrderApi from '@/api/merchant/orders'
import { listMerchantBranches } from '@/api/merchant/branches'
import { searchGlobal } from '@/api/global-search'
import {
  fetchOrderList,
  searchMerchantOrders,
  fetchOrderDetail,
  createInStoreOrder,
  createInStoreOrderQr,
  cancelInStoreOrder,
  bulkOrder,
  fetchBulkStatus,
  fetchOrderEventHistories,
  fetchStores,
  updateOrderReferenceId,
  fetchPaymentPlans,
  fetchMerchantBranches,
} from '@/api/order'
import { ORDER_STATUS } from '@/views/order/list/constants/status-map'
import { normalizeBracketFilterToNested } from '@/utils/post-filter'

const SHA256 = (pattern) =>
  String(
    crypto
      .createHash('sha256')
      .update(pattern)
      .digest('hex')
  )

// XXX dirty hack because the db value for settlement status is 'settled' or empty
// But empty string, will be removed once pushed to vue-router
// We have to patching its special value here
function applySettlementStatusHack(query) {
  if (!query || typeof query !== 'object') {
    return
  }
  if (query['settlementStatus[eq]'] === 'not_settled') {
    query['settlementStatus[eq]'] = ''
  }
  if (query.settlementStatus?.eq === 'not_settled') {
    query.settlementStatus.eq = ''
  }
}

const state = {
  list: [],
  listForBanner: [],
  detail: {},
  eventHistories: {},
  merchantStores: [],
  merchantBranches: [],
  paymentPlans: [],
  orderId: '',
  page: 1,
  limit: 25,
  queryHash: '',
  lastError: undefined,
  updatingStatus: false, // Trigger when merchant update status manually
  fetchingList: false,
  ordersTotalRecords: 0,
  approvedOrdersRecords: 0,
  authorisedOrdersRecords: 0,
  fetchingDetail: false,
  fetchingBulkOrder: false,
  bulkOrderStatus: [],
  fetchingEventHistories: false,
  downloadingOrder: false,
  eventHistoriesOrderId: undefined,
  fetchingMerchantStores: false,
  fetchingMerchantBranches: false,
  creatingInStoreOrder: false,
  drawerActiveOrderId: null,
  globalSearchOrders: [],
  globalSearchOrdersCount: 0,
  totalAllCasesRecord: 0,
  toCapture: 0,
  refunded: 0,
  totalAllCasesRecordPercentage: 0,
  toCapturePercentage: 0,
  refundedPercentage: 0,
  tabCounts: {},
}

const mutations = {
  ADD_LIST(state, { data, page }) {
    state.page = Math.max(page, 1)
    state.list = data.data
  },
  ADD_LIST_SEARCH_GLOBAL(state, { data }) {
    state.globalSearchOrders = data.data
    state.globalSearchOrdersCount = data.total
  },
  CLEAR_SEARCH_GLOBAL(state) {
    state.globalSearchOrders = []
    state.globalSearchOrdersCount = 0
  },
  ADD_LIST_BANNER(state, { data, page }) {
    state.listForBanner = data.data
  },
  REMOVE_LIST(state) {
    state.page = 1
    state.list = []
  },
  ADD_ORDER_DETAIL(state, { orderId, detail }) {
    state.detail = { ...state.detail, [orderId]: detail }
  },
  ADD_BULK_ORDER_STATUS(state, { logs }) {
    state.bulkOrderStatus = { logs }
  },
  ADD_ORDER_EVENT_HISTORIES(state, { orderId, histories }) {
    state.eventHistories = { ...state.eventHistories, [orderId]: histories }
  },
  ADD_MERCHANT_STORES(state, { merchantStores }) {
    state.merchantStores = merchantStores
  },
  ADD_MERCHANT_BRANCHES(state, { merchantBranches }) {
    state.merchantBranches = merchantBranches
  },
  SET_FETCHING(state, { name, value }) {
    state[name] = value
  },
  SET_DRAWER_ACTIVE_ORDER_ID(state, orderId) {
    state.drawerActiveOrderId = orderId
  },
  SET_TOTAL_RECORDS(state, newValue) {
    state.ordersTotalRecords = newValue
  },
  SET_APPROVED_ORDERS_RECORDS(state, newValue) {
    state.approvedOrdersRecords = newValue
  },
  SET_AUTHORISED_ORDERS_RECORDS(state, newValue) {
    state.authorisedOrdersRecords = newValue
  },
  SET_CURRENT_PAGE(state, newValue) {
    state.page = newValue
  },
  SET_ERROR(state, error) {
    state.lastError = error
  },
  SET_TOTAL_ALL_CASES_RECORD(state, newValue) {
    state.totalAllCasesRecord = newValue
  },
  SET_TO_CAPTURE(state, newValue) {
    state.toCapture = newValue
  },
  SET_REFUNDED(state, newValue) {
    state.refunded = newValue
  },
  SET_TOTAL_ALL_CASES_RECORD_PERCENTAGE(state, newValue) {
    state.totalAllCasesRecordPercentage = newValue
  },
  SET_TO_CAPTURE_PERCENTAGE(state, newValue) {
    state.toCapturePercentage = newValue
  },
  SET_COUNT(state, newValue) {
    state.tabCounts = newValue
  },

  SET_REFUNDED_PERCENTAGE(state, newValue) {
    state.refundedPercentage = newValue
  },
  SET_QUERY_HASH(state, hash) {
    state.queryHash = hash
  },
  ADD_PAYMENT_PLANS(state, { paymentPlans }) {
    state.paymentPlans = paymentPlans
  },
}

const getters = {
  getList: (state) => {
    return state.list
  },
  getListGlobalSearch: (state) => {
    return state.globalSearchOrders
  },
  getTotalGlobalSearch: (state) => {
    return state.globalSearchOrdersCount
  },
  getListForBanner: (state) => {
    return state.listForBanner
  },
  getOrdersTotalRecords: (state) => {
    return state.ordersTotalRecords
  },
  getOrderDetail: (state) => (orderId) => {
    return state.detail[orderId] || {}
  },
  drawerOrderIsActive: (state) => {
    if (!state.drawerActiveOrderId) {
      return false
    }
    const TERMINAL_STATUSES = [
      ORDER_STATUS.fully_captured,
      ORDER_STATUS.declined,
      ORDER_STATUS.expired,
    ]
    const orderDetail = state.detail[state.drawerActiveOrderId] || {}
    return !TERMINAL_STATUSES.includes(orderDetail.status)
  },
  getHistories: (state) => (orderId) => {
    return state.eventHistories[orderId] || {}
  },
  getMerchantStores: (state) => {
    return state.merchantStores
  },
  getPaymentPlans: (state) => {
    return state.paymentPlans
  },
}

const actions = {
  async fetchOrders({ commit, dispatch, state }, params) {
    try {
      const { page, limit, ...query } = params

      applySettlementStatusHack(query)

      const queryHash = SHA256(JSON.stringify(query))

      if (state.queryHash !== queryHash) {
        commit('SET_QUERY_HASH', queryHash)
        commit('REMOVE_LIST')
      }

      commit('SET_FETCHING', { name: 'fetchingList', value: true })

      if (query.merchantIds) {
        query.merchantIds = Array.isArray(query.merchantIds)
          ? query.merchantIds
          : [query.merchantIds]
      }

      const { data } = await fetchOrderList({ page, limit, ...query })
      commit('SET_COUNT', data.tabCounts)
      if (query.withListingData) {
        commit('SET_TOTAL_ALL_CASES_RECORD', data.totalAllCasesRecord || 0)
        commit('SET_TO_CAPTURE', data.toCapture || 0)
        commit('SET_REFUNDED', data.refunded || 0)
        commit(
          'SET_TOTAL_ALL_CASES_RECORD_PERCENTAGE',
          data.totalAllCasesRecordPercentage || 0
        )
        commit('SET_TO_CAPTURE_PERCENTAGE', data.toCapturePercentage || 0)
        commit('SET_REFUNDED_PERCENTAGE', data.refundedPercentage || 0)
      }
      commit('SET_FETCHING', { name: 'fetchingList', value: false })
      commit('ADD_LIST', { data, page })
      commit('SET_CURRENT_PAGE', page)
      commit('SET_TOTAL_RECORDS', data.total)
    } catch (error) {
      commit('SET_ERROR', error)
      commit('SET_FETCHING', { name: 'fetchingList', value: false })
    }
  },
  async fetchOrdersPost({ commit, state }, { page, limit, filter = {} } = {}) {
    try {
      let query = { ...filter }

      query = normalizeBracketFilterToNested(query)
      applySettlementStatusHack(query)

      const queryHash = SHA256(JSON.stringify(query))

      if (state.queryHash !== queryHash) {
        commit('SET_QUERY_HASH', queryHash)
        commit('REMOVE_LIST')
      }

      commit('SET_FETCHING', { name: 'fetchingList', value: true })

      if (query.merchantIds) {
        query.merchantIds = Array.isArray(query.merchantIds)
          ? query.merchantIds
          : [query.merchantIds]
      }

      const { data } = await searchMerchantOrders({
        page,
        limit,
        withListingData: true,
        filter: query,
      })
      commit('SET_COUNT', data.tabCounts)
      if (data.totalAllCasesRecord !== undefined) {
        commit('SET_TOTAL_ALL_CASES_RECORD', data.totalAllCasesRecord || 0)
      }
      if (data.toCapture !== undefined) {
        commit('SET_TO_CAPTURE', data.toCapture || 0)
      }
      if (data.refunded !== undefined) {
        commit('SET_REFUNDED', data.refunded || 0)
      }
      if (data.totalAllCasesRecordPercentage !== undefined) {
        commit(
          'SET_TOTAL_ALL_CASES_RECORD_PERCENTAGE',
          data.totalAllCasesRecordPercentage || 0
        )
      }
      if (data.toCapturePercentage !== undefined) {
        commit('SET_TO_CAPTURE_PERCENTAGE', data.toCapturePercentage || 0)
      }
      if (data.refundedPercentage !== undefined) {
        commit('SET_REFUNDED_PERCENTAGE', data.refundedPercentage || 0)
      }
      commit('SET_FETCHING', { name: 'fetchingList', value: false })
      commit('ADD_LIST', { data, page })
      commit('SET_CURRENT_PAGE', page)
      commit('SET_TOTAL_RECORDS', data.total)
    } catch (error) {
      commit('SET_ERROR', error)
      commit('SET_FETCHING', { name: 'fetchingList', value: false })
    }
  },
  async searchGlobal({ commit }, value) {
    commit('SET_FETCHING', { name: 'fetchingList', value: true })
    const result = await searchGlobal(value)
    commit('SET_FETCHING', { name: 'fetchingList', value: false })
    commit('ADD_LIST_SEARCH_GLOBAL', {
      data: result?.data?.data?.merchantOrders,
    })
  },
  async clearSearchResult({ commit }, value) {
    commit('CLEAR_SEARCH_GLOBAL')
  },
  async fetchOrdersForBanner({ commit, dispatch, state }, params) {
    try {
      const { page, limit, ...query } = params

      if (query['settlementStatus[eq]'] === 'not_settled') {
        // XXX dirty hack because the db value for settlement status is 'settled' or empty
        // But empty string, will be removed once pushed to vue-router
        // We have to patching its special value here
        query['settlementStatus[eq]'] = ''
      }

      const queryHash = SHA256(JSON.stringify(query))

      if (state.queryHash !== queryHash) {
        commit('SET_QUERY_HASH', queryHash)
        commit('REMOVE_LIST')
      }

      commit('SET_FETCHING', { name: 'fetchingList', value: true })

      if (query.merchantIds) {
        query.merchantIds = Array.isArray(query.merchantIds)
          ? query.merchantIds
          : [query.merchantIds]
      }

      const { data } = await fetchOrderList({ page, limit, ...query })

      commit('SET_FETCHING', { name: 'fetchingList', value: false })
      commit('ADD_LIST_BANNER', { data, page })
    } catch (error) {
      commit('SET_ERROR', error)
      commit('SET_FETCHING', { name: 'fetchingList', value: false })
    }
  },
  async fetchOrdersRecordsCount({ commit }) {
    try {
      const approvedOrders = await fetchOrderList({
        'status[eq]': 'approved',
        page: 1,
        limit: 1,
      })
      const approvedOrdersRecords = approvedOrders.data.total ?? 0
      commit('SET_APPROVED_ORDERS_RECORDS', approvedOrdersRecords)

      const authorisedOrders = await fetchOrderList({
        'status[eq]': 'authorised',
        page: 1,
        limit: 1,
      })
      const authorisedOrdersRecords = authorisedOrders.data.total ?? 0
      commit('SET_AUTHORISED_ORDERS_RECORDS', authorisedOrdersRecords)
    } catch (error) {
      commit('SET_ERROR', error)
    }
  },
  async fetchOrderDetail({ commit }, data) {
    const orderId = data.orderId
    try {
      commit('SET_FETCHING', { name: 'fetchingDetail', value: true })

      const response = await fetchOrderDetail(data)
      commit('SET_FETCHING', { name: 'fetchingDetail', value: false })
      commit('ADD_ORDER_DETAIL', { orderId, detail: response.data })
    } catch (error) {
      commit('SET_ERROR', error)
      commit('SET_FETCHING', { name: 'fetchingDetail', value: false })
    }
  },
  async fetchBulkStatus({ commit }, data) {
    try {
      commit('SET_FETCHING', { name: 'fetchingBulkOrder', value: true })

      const response = await fetchBulkStatus(data)
      commit('SET_FETCHING', { name: 'fetchingBulkOrder', value: false })
      commit('ADD_BULK_ORDER_STATUS', { logs: response.data.logs })
      return response
    } catch (error) {
      commit('SET_ERROR', error)
      commit('SET_FETCHING', { name: 'fetchingBulkOrder', value: false })
    }
  },

  async createInStoreOrder({ commit }, data) {
    try {
      commit('SET_FETCHING', { name: 'creatingInStoreOrder', value: true })

      const response = await createInStoreOrder(data)

      commit('SET_FETCHING', { name: 'creatingInStoreOrder', value: false })

      return response.data
    } catch (error) {
      commit('SET_ERROR', error)
      commit('SET_FETCHING', { name: 'creatingInStoreOrder', value: false })

      throw error
    }
  },

  async createInStoreOrderQr({ commit }, data) {
    try {
      commit('SET_FETCHING', { name: 'creatingInStoreOrder', value: true })

      const response = await createInStoreOrderQr(data)

      commit('SET_FETCHING', { name: 'creatingInStoreOrder', value: false })

      return response.data
    } catch (error) {
      commit('SET_ERROR', error)
      commit('SET_FETCHING', { name: 'creatingInStoreOrder', value: false })

      throw error
    }
  },

  async cancelInStoreOrder({ commit }, data) {
    try {
      commit('SET_FETCHING', { name: 'cancellingInStoreOrder', value: true })

      const response = await cancelInStoreOrder(data)

      commit('SET_FETCHING', { name: 'cancellingInStoreOrder', value: false })

      return response.data
    } catch (error) {
      commit('SET_ERROR', error)
      commit('SET_FETCHING', { name: 'cancellingInStoreOrder', value: false })

      throw error
    }
  },

  async bulkOrder({ commit }, data) {
    try {
      commit('SET_FETCHING', { name: 'bulkingOrder', value: true })

      const response = await bulkOrder(data)

      commit('SET_FETCHING', { name: 'bulkingOrder', value: false })

      return response.data
    } catch (error) {
      commit('SET_ERROR', error)
      commit('SET_FETCHING', { name: 'bulkingOrder', value: false })

      throw error
    }
  },

  async fetchMerchantStores({ commit }) {
    try {
      commit('SET_FETCHING', { name: 'fetchingStores', value: true })

      const response = await fetchStores()

      commit('SET_FETCHING', { name: 'fetchingStores', value: false })
      commit('ADD_MERCHANT_STORES', { merchantStores: response.data })
    } catch (error) {
      commit('SET_ERROR', error)
    }
  },

  async fetchMerchantBranches({ commit }, merchantIds) {
    try {
      commit('SET_FETCHING', { name: 'fetchingMerchantBranches', value: true })

      if (
        !merchantIds ||
        !Array.isArray(merchantIds) ||
        merchantIds.length === 0
      ) {
        return
      }
      const params = merchantIds.map((id) => `merchantIds[]=${id}`).join('&')

      const response = await fetchMerchantBranches(params)

      commit('SET_FETCHING', { name: 'fetchingMerchantBranches', value: false })
      commit('ADD_MERCHANT_BRANCHES', { merchantBranches: response.data.data })
    } catch (error) {
      commit('SET_ERROR', error)
      commit('SET_FETCHING', { name: 'fetchingMerchantBranches', value: false })
    }
  },

  async fetchMerchantBranchesPost({ commit }, { filter } = {}) {
    try {
      commit('SET_FETCHING', { name: 'fetchingMerchantBranches', value: true })

      const merchantIds = filter?.merchantIds
      if (
        !merchantIds ||
        !Array.isArray(merchantIds) ||
        merchantIds.length === 0
      ) {
        commit('SET_FETCHING', {
          name: 'fetchingMerchantBranches',
          value: false,
        })
        return
      }

      const response = await listMerchantBranches({ filter })

      commit('SET_FETCHING', { name: 'fetchingMerchantBranches', value: false })
      commit('ADD_MERCHANT_BRANCHES', { merchantBranches: response.data.data })
    } catch (error) {
      commit('SET_ERROR', error)
      commit('SET_FETCHING', { name: 'fetchingMerchantBranches', value: false })
    }
  },

  async fetchOrderEventHistories({ commit }, params) {
    const orderId = params.orderId
    if (!orderId) {
      return
    }

    try {
      commit('SET_FETCHING', { name: 'fetchingEventHistories', value: true })

      const response = await fetchOrderEventHistories(params)

      commit('SET_FETCHING', { name: 'fetchingEventHistories', value: false })
      commit('ADD_ORDER_EVENT_HISTORIES', { orderId, histories: response.data })
    } catch (error) {
      commit('SET_ERROR', error)
    }
  },

  async updateStatusOrder({ commit, dispatch }, { orderId, payload, action }) {
    try {
      commit('SET_FETCHING', { name: 'updatingStatus', value: true })

      const actionHandler = {
        authorise: OrdersStatusApi.authoriseOrder,
        capture: MerchantOrderApi.captureOrder,
        cancel: MerchantOrderApi.cancelOrder,
        refund: MerchantOrderApi.refundOrder,
        disputeCancel: MerchantOrderApi.disputeCancelOrder,
      }[action]

      if (typeof actionHandler !== 'function') {
        throw new Error('The action is not allowed!')
      }

      const response = await actionHandler(orderId, payload)

      // Trigger fetch new status
      const { merchantId } = payload
      await fetchOrderDetail({ orderId, merchantId })

      commit('SET_FETCHING', { name: 'updatingStatus', value: false })

      return response
    } catch (error) {
      commit('SET_ERROR', error)
      commit('SET_FETCHING', { name: 'updatingStatus', value: false })

      throw error
    }
  },

  async downloadOrders({ commit }, data) {
    try {
      commit('SET_FETCHING', { name: 'downloadingOrder', value: true })

      const { fromDate, toDate, timezone, ...rawFilter } = data || {}

      const filter = normalizeBracketFilterToNested({ ...rawFilter })
      applySettlementStatusHack(filter)

      const payload = {
        fromDate,
        toDate,
        timezone,
        withListingData: true,
        filter,
      }

      const response = await MerchantOrderApi.downloadOrders(payload)

      commit('SET_FETCHING', { name: 'downloadingOrder', value: false })

      return response.data
    } catch (error) {
      commit('SET_ERROR', error)
      commit('SET_FETCHING', { name: 'downloadingOrder', value: false })

      throw error
    }
  },

  async updateOrderReferenceId({ commit, dispatch }, data) {
    if (!data.orderId) {
      return
    }

    try {
      const response = await updateOrderReferenceId(data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error)
    }
  },

  async fetchPaymentPlans({ commit }, data) {
    try {
      commit('SET_FETCHING', { name: 'fetchingPaymentPlans', value: true })

      const response = await fetchPaymentPlans(data)

      commit('SET_FETCHING', { name: 'fetchingPaymentPlans', value: false })
      if (response.data && response.data.length === 0) {
        commit('ADD_PAYMENT_PLANS', {
          paymentPlans: [{ name: 'PAY_BY_INSTALMENTS' }],
        })
      } else {
        commit('ADD_PAYMENT_PLANS', { paymentPlans: response.data })
      }

      return response.data
    } catch (error) {
      commit('SET_ERROR', error)
      commit('SET_FETCHING', { name: 'fetchingPaymentPlans', value: false })

      throw error
    }
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
